import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./ShopPage.module.scss";
import ProductCard from "@/components/ProductCard/ProductCard";
import Nav from "@/components/Header/Nav/Nav";
import Footer from "@/components/Footer/Footer";
import CategoryList from "@/components/Shop/CategoryList";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import CategoryMenu from "@/components/CategoryMenu/CategoryMenu.tsx";
import type { KeyboardEvent, ChangeEvent } from "react";
import { Helmet } from "react-helmet-async";
import { formatName } from "@/utils/formatName";
import { getSiteConfig } from "@/config/siteConfigs";

const CRM_API = "https://crm.hixa.eu/external/goodsapi/";
const CRM_TOKEN = "DH345DFGDGb32FDsf03876SDFSdf13";

const ShopPage: React.FC = () => {
  const { t } = useTranslation("pages");
  const siteConfig = getSiteConfig();
  const [products, setProducts] = useState<any[]>([]);
  const [totalProducts, setTotalProducts] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchParams] = useSearchParams();
  const { categoryId, section } = useParams<{
    categoryId?: string;
    section?: string;
  }>();

  const table = section === "services" ? "service" : "good";
  const selectedCategory = categoryId || null;
  const searchQuery = searchParams.get("search")?.trim() || "";

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState<string>("default");
  const [query, setQuery] = useState(searchQuery);
  const [categoryTree, setCategoryTree] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);
  const ITEMS_PER_PAGE = 12;

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setQuery(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    const fetchCategoryTree = async () => {
      if (!selectedCategory) {
        setCategoryTree({});
        return;
      }

      try {
        const params: Record<string, string> = {
          type: "category",
          catid: selectedCategory,
          legid: "1",
          language: siteConfig.language,
          table: table,
        };

        const response = await fetch(CRM_API, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${CRM_TOKEN}`,
            "Content-Type": "application/x-www-form-urlencoded",
            Accept: "application/json",
          },
          body: new URLSearchParams(params),
        });

        const data = await response.json();
        if (data.success && data.tree) {
          setCategoryTree(data.tree);
        }
      } catch (error) {
        console.error("Error fetching category tree:", error);
      }
    };

    fetchCategoryTree();
  }, [selectedCategory, section, siteConfig.language]);

  const fetchProducts = async () => {
    if (loading) return;
    setLoading(true);

    const categoryId = selectedCategory || "0";

    const params: Record<string, string> = {
      type: "good",
      onlyprice: "1",
      start: ((page - 1) * ITEMS_PER_PAGE).toString(),
      limit: ITEMS_PER_PAGE.toString(),
      legid: "1",
      language: siteConfig.language,
      table: table,
      catid: categoryId,
    };

    if (searchQuery) {
      params.search = searchQuery;
    }

    const res = await fetch(CRM_API, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${CRM_TOKEN}`,
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
      },
      body: new URLSearchParams(params),
    });

    const data = await res.json();

    if (data.success && data.data) {
      setProducts(data.data);
      setTotalProducts(data.total);
    }

    setLoading(false);
  };

  useEffect(() => {
    setPage(1);
  }, [selectedCategory, searchQuery, section]);

  useEffect(() => {
    fetchProducts();
    window.scrollTo(0, 0);
  }, [page, selectedCategory, searchQuery, section]);

  const totalPages = Math.ceil(totalProducts / ITEMS_PER_PAGE);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (page > 3) {
        pages.push("...");
      }

      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (page < totalPages - 2) {
        pages.push("...");
      }

      pages.push(totalPages);
    }

    return pages;
  };


  const sortProducts = (products: any[], order: string) => {
    if (order === "_price_ASC") {
      return products.sort((a, b) => a.nettoprice - b.nettoprice);
    }
    if (order === "_price_DESC") {
      return products.sort((a, b) => b.nettoprice - a.nettoprice);
    }
    return products;
  };

  const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newOrder = event.target.value;
    setSortOrder(newOrder);
    const sortedProducts = sortProducts([...products], newOrder);
    setProducts(sortedProducts);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const handleSearch = () => {
    const trimmed = query.trim();
    const params = new URLSearchParams();

    if (trimmed) {
      params.set("search", trimmed);
    }

    if (selectedCategory && categoryTree[selectedCategory]) {
      const categoryName = categoryTree[selectedCategory];
      const formattedName = formatName(categoryName);
      const queryString = params.toString();
      const sectionPath = section || "shop";
      const url = queryString
        ? `/shop/${sectionPath}/${selectedCategory}/${formattedName}?${queryString}`
        : `/shop/${sectionPath}/${selectedCategory}/${formattedName}`;
      navigate(url);
    } else {
      navigate(`/shop?${params.toString()}`);
    }
  };

  const schemaOrgData = {
    "@context": "https://schema.org",
    "@type": "Store",
    name: "Hixa",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Neukirchstraße 74",
      addressLocality: "Berlin",
      postalCode: "13089",
      addressCountry: "DE",
    },
    openingHours: [
      "Mo 08:00-12:00, 13:00-17:00",
      "Tu 08:00-12:00, 13:00-17:00",
      "We 08:00-12:00, 13:00-17:00",
      "Th 08:00-12:00, 13:00-17:00",
      "Fr 08:00-12:00, 13:00-17:00",
      "Sa 09:00-13:00",
      "Su Closed",
    ],
    telephone: "+493052015191",
    url: "https://hixa.eu/shop/",
  };

  return (
    <div className="pageSection">
      <Helmet>
        <title>{t("shop.meta.title")}</title>
        <meta name="description" content={t("shop.meta.description")} />
        <script type="application/ld+json">
          {JSON.stringify(schemaOrgData)}
        </script>
      </Helmet>
      {siteConfig.domain === "hixa.eu" && (
        <Helmet>
          <meta name="google-shopping" content="noshow" />
        </Helmet>
      )}
      <Nav />

      <div className="container">
        <div className={styles.shopContainer}>
          {searchQuery && (
            <div className={styles.searchTitle}>
              {t("shop.search.resultsFor")} <strong>{searchQuery}</strong>
            </div>
          )}
          <div className="sideP88 titleCont">
            <div className={`titleBlock ${styles.shopTitleBlock}`}>
              <span className={styles.navigation}>
                <a href="/">{t("shop.navigation.home")}</a>
                <span className={styles.separator}>/</span>
                <a href="/shop">{t("shop.navigation.shop")}</a>

                {Object.entries(categoryTree).map(([catId, catName]) => (
                  <React.Fragment key={catId}>
                    <span className={styles.separator}>/</span>
                    <a
                      href={`/shop/${section || "shop"}/${catId}/${formatName(
                        catName,
                      )}`}
                    >
                      {catName}
                    </a>
                  </React.Fragment>
                ))}
              </span>
            </div>
          </div>
          <div className={`${styles.mainContent} sideP88`}>
            {screenWidth > 1025 && (
              <div className={styles.mainCategoryBlock}>
                <CategoryList isMenu={false} />
              </div>
            )}

            <div className={styles.mainProductBlock}>
              <div className={styles.controlsPanel}>
                {screenWidth <= 1025 && (
                  <>
                    <div className={` textBasic ${styles.filterTitle}`}>
                      {t("shop.filter.title")}
                    </div>
                    <div className={styles.searchBar}>
                      <input
                        type="text"
                        value={query}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        placeholder={t("shop.search.placeholder")}
                      />
                      <button
                        className={styles.button}
                        aria-label={t("shop.search.button")}
                        onClick={handleSearch}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 512 512"
                        >
                          <path d="M495,466.2L377.2,348.4c29.2-35.6,46.8-81.2,46.8-130.9C424,103.5,331.5,11,217.5,11C103.4,11,11,103.5,11,217.5S103.4,424,217.5,424c49.7,0,95.2-17.5,130.8-46.7L466.1,495c8,8,20.9,8,28.9,0C503,487.1,503,474.1,495,466.2z M217.5,382.9C126.2,382.9,52,308.7,52,217.5S126.2,52,217.5,52C308.7,52,383,126.3,383,217.5S308.7,382.9,217.5,382.9z" />
                        </svg>
                      </button>
                    </div>
                    <button
                      onClick={() => setIsMenuOpen(true)}
                      className={styles.categoryButton}
                    >
                      {t("shop.filter.button")}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        width="26.5"
                        height="32"
                        style={{
                          fill: "black",
                          width: "100%",
                          height: "100%",
                          display: "block",
                        }}
                      >
                        <path d="M64 144h226.75a48 48 0 0090.5 0H448a16 16 0 000-32h-66.75a48 48 0 00-90.5 0H64a16 16 0 000 32zM448 368h-66.75a48 48 0 00-90.5 0H64a16 16 0 000 32h226.75a48 48 0 0090.5 0H448a16 16 0 000-32zM448 240H221.25a48 48 0 00-90.5 0H64a16 16 0 000 32h66.75a48 48 0 0090.5 0H448a16 16 0 000-32z" />
                      </svg>
                    </button>
                  </>
                )}
              </div>

              <select
                id="sort-filter"
                className={styles.filterSelect}
                data-brx-filter="true"
                name="form-field-lcyvbw"
                value={sortOrder}
                onChange={handleSortChange}
              >
                <option value="default">{t("shop.filter.sortDefault")}</option>
                <option value="_price_ASC">
                  {t("shop.filter.sortPriceAsc")}
                </option>
                <option value="_price_DESC">
                  {t("shop.filter.sortPriceDesc")}
                </option>
              </select>

              <div className={styles.productList}>
                {products.map((product) => {
                  if (!product.goid) console.warn("Missing goid in:", product);
                  const imageUrl = product.files?.image
                    ? (Object.values(product.files.image)[0] as string)
                    : undefined;
                  return (
                    <ProductCard
                      key={product.goid}
                      goid={product.goid}
                      name={product.name}
                      price={product.nettoprice}
                      table={product.table}
                      imageUrl={imageUrl}
                    />
                  );
                })}
              </div>

              <div className={styles.productCount}>
                {t("shop.results.showing")} {(page - 1) * ITEMS_PER_PAGE + 1} -{" "}
                {Math.min(page * ITEMS_PER_PAGE, totalProducts)}{" "}
                {t("shop.results.of")} {totalProducts} {t("shop.results.total")}
              </div>

              {totalPages > 1 && (
                <div className={styles.pagination}>
                  <button
                    className={styles.paginationArrow}
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                  >
                    &lsaquo;
                  </button>

                  {getPageNumbers().map((pageNum, index) =>
                    pageNum === "..." ? (
                      <span key={`dots-${index}`} className={styles.paginationDots}>
                        ...
                      </span>
                    ) : (
                      <button
                        key={pageNum}
                        className={`${styles.paginationButton} ${
                          page === pageNum ? styles.active : ""
                        }`}
                        onClick={() => handlePageChange(pageNum as number)}
                      >
                        {pageNum}
                      </button>
                    )
                  )}

                  <button
                    className={styles.paginationArrow}
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                  >
                    &rsaquo;
                  </button>
                </div>
              )}

              {loading && <div className={styles.loading}>{t("shop.loading")}</div>}
            </div>
          </div>
        </div>
      </div>

      <CategoryMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />

      <Footer></Footer>
    </div>
  );
};

export default ShopPage;
